// components/SiteGroupManager.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from '../../../generic_components/Modal/Modal';
import Card from '../../../generic_components/Card/Card';
import Button from '../../../generic_components/Button/Button';
import Select from '../../../generic_components/Select/Select'; // NEW: Import custom Select
import GenericTooltip from '../../../generic_components/Tooltip/Tooltip';
import { useToast } from '../../../generic_components/Toast/Toast';
import { BeatLoader } from 'react-spinners';
import { url } from '../../../api/endpointURL';
import './SiteGroupManager.css';

const SiteGroupManager = ({ selectedBusiness, allSites, userEmail, onClose }) => {
  const [groups, setGroups] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isWarningEndAllGroupModalOpen, setIsWarningEndAllGroupModalOpen] = useState(false);
  const [isWarningEndGroupModalOpen, setIsWarningEndGroupModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [selectedSitesForNewGroup, setSelectedSitesForNewGroup] = useState([]);
  const [isRemoveSitesModalOpen, setIsRemoveSitesModalOpen] = useState(false);
  const [removingGroup, setRemovingGroup] = useState(null);
  const [isRemovingSites, setIsRemovingSites] = useState(false);
  const [showUngroupedOnly, setShowUngroupedOnly] = useState(false);
   const [showUngroupedOnlyModal, setShowUngroupedOnlyModal] = useState(false);
  
  // Selection states for actions
  const [selectedSitesInGroups, setSelectedSitesInGroups] = useState({});
  const [selectedSitesInAvailable, setSelectedSitesInAvailable] = useState([]);
  const [moveToGroupId, setMoveToGroupId] = useState('');
  const [movingFromGroup, setMovingFromGroup] = useState(null);
  
  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [movingMessage, setMovingMessage] = useState('');

  const toast = useToast();
  const MINIMUM_WAIT_TIME = 1000;

  const withMinimumWait = useCallback(async (promise, startTime) => {
    const result = await promise;
    const elapsed = Date.now() - startTime;
    const remainingTime = MINIMUM_WAIT_TIME - elapsed;
    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }
    return result;
  }, []);

  // Compute grouped site names (from all groups)
  const groupedSiteSet = useMemo(() => {
  const set = new Set();

  groups.forEach(group => {
    group.sites?.forEach(siteObj => {
      set.add(siteObj.site_name);
    });
  });

  return set;
}, [groups]);

  const fetchGroups = useCallback(async () => {
    try {
      const startTime = Date.now();
      setIsInitialLoading(true);
      const response = await fetch(`${url}/site-groups/${encodeURIComponent(selectedBusiness)}`);
      if (response.ok) {
        const data = await response.json();
        await withMinimumWait(Promise.resolve(), startTime);
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setIsInitialLoading(false);
    }
  }, [selectedBusiness, withMinimumWait]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const availableSites = useMemo(() => {
    return allSites.filter(site => site !== 'All');
  }, [allSites]);

  const availableSitesForNewGroupFiltered = useMemo(() => {
    const sites = showUngroupedOnlyModal
    ? availableSites.filter(site => !groupedSiteSet.has(site))
    : availableSites;

     return [...sites].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
  }, [availableSites, groupedSiteSet, showUngroupedOnlyModal]);

  // Filter Available Sites based on toggle
const filteredAvailableSites = useMemo(() => {
  const sites = showUngroupedOnly
    ? availableSites.filter(site => !groupedSiteSet.has(site))
    : availableSites;

  return [...sites].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}, [availableSites, groupedSiteSet, showUngroupedOnly]);


  // NEW: Prepare options for move-to-group dropdown
  const moveToGroupOptions = useMemo(() => {
    return groups
      .filter(g => g.id !== movingFromGroup)
      .map(group => ({
        value: group.id,
        label: group.group_name
      }));
  }, [groups, movingFromGroup]);

  // Toggle site selection in a group
  const toggleSiteSelectionInGroup = (groupId, siteName) => {
    setSelectedSitesInGroups(prev => {
      const currentSelections = prev[groupId] || [];
      const isSelected = currentSelections.includes(siteName);
      
      if (isSelected) {
        const newSelections = currentSelections.filter(s => s !== siteName);
        return { ...prev, [groupId]: newSelections };
      } else {
        return { ...prev, [groupId]: [...currentSelections, siteName] };
      }
    });
  };

  // Toggle site selection in available sites
  const toggleSiteSelectionInAvailable = (siteName) => {
    setSelectedSitesInAvailable(prev => 
      prev.includes(siteName) 
        ? prev.filter(s => s !== siteName)
        : [...prev, siteName]
    );
  };

  // Clear all selections for a group
  const clearGroupSelections = (groupId) => {
    setSelectedSitesInGroups(prev => ({ ...prev, [groupId]: [] }));
  };

  // Clear available sites selections
  const clearAvailableSelections = () => {
    setSelectedSitesInAvailable([]);
  };

  const handleDeleteSitesFromGroup = (groupId, groupName) => {
  const selectedSites = selectedSitesInGroups[groupId] || [];
  if (selectedSites.length === 0) return;

  setRemovingGroup({ groupId, groupName });
  setIsRemoveSitesModalOpen(true);
};

const confirmRemoveSitesFromGroup = async () => {
  if (isRemovingSites || !removingGroup) return;

  const { groupId, groupName } = removingGroup;
  const selectedSites = selectedSitesInGroups[groupId] || [];

  if (selectedSites.length === 0) return;

  const startTime = Date.now();
  setIsRemovingSites(true);

  try {
    const response = await fetch(
      `${url}/site-groups/group/${groupId}/remove-sites`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sites: selectedSites,
          businessName: selectedBusiness
        })
      }
    );

    if (response.ok) {
      await withMinimumWait(fetchGroups(), startTime);
      clearGroupSelections(groupId);

      toast.success(
        `${selectedSites.length} site(s) removed from "${groupName}"`,
        { autoClose: 3000 }
      );

      setIsRemoveSitesModalOpen(false);
    } else {
      toast.error('Failed to remove sites from group', { autoClose: 5000 });
    }
  } catch (error) {
    console.error('Error removing sites:', error);
    toast.error('Failed to remove sites from group', { autoClose: 5000 });
  } finally {
    setIsRemovingSites(false);
  }
};


  // Move selected sites to another group
  const handleMoveSelectedSites = async () => {
    if (!moveToGroupId || !movingFromGroup) return;

    const selectedSites = selectedSitesInGroups[movingFromGroup] || [];
    if (selectedSites.length === 0) return;

    // Check if any sites already exist in target group
    const targetGroup = groups.find(g => g.id === Number(moveToGroupId));
    const sitesAlreadyInTarget = selectedSites.filter(site =>
      targetGroup?.sites?.some(s => s.site_name === site)
    );

    if (sitesAlreadyInTarget.length > 0) {
      toast.warning(`${sitesAlreadyInTarget.length} site(s) already in "${targetGroup.group_name}"`, {
        autoClose: 3000
      });
      return;
    }

    const startTime = Date.now();
    setIsMoving(true);
    setMovingMessage(`Moving ${selectedSites.length} site(s)...`);

    try {
      const payload = {
        sites: selectedSites.map(s => String(s)),
        targetGroupId: Number(moveToGroupId),
        sourceGroupId: null,
        businessName: String(selectedBusiness)
      };

      const response = await fetch(`${url}/site-groups/move-sites`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await withMinimumWait(fetchGroups(), startTime);
        clearGroupSelections(movingFromGroup);
        setIsMoveModalOpen(false);
        toast.success(`${selectedSites.length} site(s) added to group`, { autoClose: 3000 });
      } else {
        toast.error('Failed to move sites', { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error moving sites:', error);
      toast.error('Failed to move sites', { autoClose: 5000 });
    } finally {
      setIsMoving(false);
      setMovingMessage('');
    }
  };

  // Add selected available sites to a group
  const handleAddSitesToGroup = async () => {
    if (!moveToGroupId || selectedSitesInAvailable.length === 0) return;

    // Check if any sites already exist in target group
    const targetGroup = groups.find(g => g.id === Number(moveToGroupId));
    const sitesAlreadyInTarget = selectedSitesInAvailable.filter(site =>
      targetGroup?.sites?.some(s => s.site_name === site)
    );

    if (sitesAlreadyInTarget.length > 0) {
      toast.warning(`${sitesAlreadyInTarget.length} site(s) already in "${targetGroup.group_name}"`, {
        autoClose: 3000
      });
      return;
    }

    const startTime = Date.now();
    setIsMoving(true);
    setMovingMessage(`Adding ${selectedSitesInAvailable.length} site(s)...`);

    try {
      const payload = {
        sites: selectedSitesInAvailable.map(s => String(s)),
        targetGroupId: Number(moveToGroupId),
        sourceGroupId: null,
        businessName: String(selectedBusiness)
      };

      const response = await fetch(`${url}/site-groups/move-sites`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await withMinimumWait(fetchGroups(), startTime);
        clearAvailableSelections();
        setIsMoveModalOpen(false);
        toast.success(`${selectedSitesInAvailable.length} site(s) added to group`, { autoClose: 3000 });
      } else {
        toast.error('Failed to add sites', { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error adding sites:', error);
      toast.error('Failed to add sites', { autoClose: 5000 });
    } finally {
      setIsMoving(false);
      setMovingMessage('');
    }
  };

  // CREATE: Create new group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.warning('Please enter a group name', { autoClose: 5000 });
      return;
    }
    if (selectedSitesForNewGroup.length === 0) {
      toast.warning('Please select at least one site', { autoClose: 5000 });
      return;
    }

    const startTime = Date.now();
    setIsCreating(true);

    try {
      const response = await fetch(`${url}/site-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: selectedBusiness,
          groupName: newGroupName,
          sites: selectedSitesForNewGroup,
          createdBy: userEmail
        })
      });

      if (response.ok) {
        await withMinimumWait(fetchGroups(), startTime);
        handleCloseCreateModal();
        toast.success(`Group "${newGroupName}" created successfully!`, { autoClose: 5000 });
      } else {
        const error = await response.json();
        const errorMsg = error.error || 'Failed to create group';
        toast.error(errorMsg, { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group', { autoClose: 5000 });
    } finally {
      setIsCreating(false);
    }
  };

  // UPDATE: Update group name
  const handleUpdateGroupName = async () => {
    if (!editingGroup || !editingGroup.newName.trim()) {
      toast.warning('Please enter a valid group name', { autoClose: 5000 });
      return;
    }

    const startTime = Date.now();
    setIsEditing(true);

    try {
      const response = await fetch(`${url}/site-groups/group/${editingGroup.id}/name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupName: editingGroup.newName,
          businessName: selectedBusiness
        })
      });

      if (response.ok) {
        await withMinimumWait(fetchGroups(), startTime);
        handleCloseEditModal();
        toast.success('Group name updated successfully!', { autoClose: 5000 });
      } else {
        const error = await response.json();
        const errorMsg = error.error || 'Failed to update group name';
        toast.error(errorMsg, { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error updating group name:', error);
      toast.error('Failed to update group name', { autoClose: 5000 });
    } finally {
      setIsEditing(false);
    }
  };

  // DELETE: Delete a specific group
  const handleDeleteGroupWarning = (groupId, groupName) => {
    setIsWarningEndGroupModalOpen(true);
    setDeletingGroup({ groupId, groupName });
  };

  const handleEndGroup = async () => {
    const startTime = Date.now();
    setIsDeleting(true);

    try {
      const groupId = deletingGroup.groupId;
      const groupName = deletingGroup.groupName;
      const response = await fetch(`${url}/site-groups/group/${groupId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await withMinimumWait(fetchGroups(), startTime);
        handleCloseWarningEndGroupModal();
        toast.success(`Group "${groupName}" deleted successfully!`, { autoClose: 5000 });
      } else {
        toast.error('Failed to delete group', { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group', { autoClose: 5000 });
    } finally {
      setIsDeleting(false);
    }
  };

  // DELETE: End all grouping
  const handleEndAllGroup = async () => {
    const startTime = Date.now();
    setIsDeletingAll(true);

    try {
      const response = await fetch(`${url}/site-groups/business/${encodeURIComponent(selectedBusiness)}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await withMinimumWait(Promise.resolve(), startTime);
        setGroups([]);
        handleCloseWarningEndAllGroupModal();
        toast.success('All grouping ended successfully!', { autoClose: 5000 });
      } else {
        toast.error('Failed to end grouping', { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error ending grouping:', error);
      toast.error('Failed to end grouping', { autoClose: 5000 });
    } finally {
      setIsDeletingAll(false);
    }
  };

  // Toggle site selection for new group
  const toggleSiteSelection = (site) => {
    setSelectedSitesForNewGroup(prev =>
      prev.includes(site)
        ? prev.filter(s => s !== site)
        : [...prev, site]
    );
  };


  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewGroupName('');
    setSelectedSitesForNewGroup([]);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingGroup(null);
  };

  const handleCloseWarningEndAllGroupModal = () => {
    setIsWarningEndAllGroupModalOpen(false);
  };

  const handleCloseWarningEndGroupModal = () => {
    setIsWarningEndGroupModalOpen(false);
  };

  const handleCloseMoveModal = () => {
    setIsMoveModalOpen(false);
    setMoveToGroupId('');
    setMovingFromGroup(null);
  };

  return (
    <div className="site-group-manager">
      {/* Loading Overlay */}
      {isMoving && (
        <div className="loading-overlay-beatloader">
          <div className="loading-content">
            <BeatLoader size={10} color="#36d7b7" />
            <p className="loading-message">{movingMessage}</p>
          </div>
        </div>
      )}

      <div className="manager-header">
        <div className="header-actions">
          <Button
            variant='gradient'
            onClick={() => setIsCreateModalOpen(true)}
            disabled={availableSitesForNewGroupFiltered.length === 0 || isMoving}
          >
            + Create New Group
          </Button>
          {groups.length > 0 && (
            <Button
              variant='danger'
              onClick={() => setIsWarningEndAllGroupModalOpen(true)}
              disabled={isMoving}
            >
              End All Grouping
            </Button>
          )}
        </div>
      </div>

      {/* Initial Loading State */}
      {isInitialLoading ? (
        <div className="initial-loading-container">
          <BeatLoader size={15} color="#36d7b7" />
          <p>Loading groups...</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="empty-state">
          <p>No groups created yet. Create your first group to organize sites!</p>
          {allSites.length === 0 && <p className="warning">No sites available for this business.</p>}
        </div>
      ) : (
        <>
          <div className='instruction-text'>
         <i class="fa-sharp fa-solid fa-circle-info"></i>
          <span> Select sites and use actions to manage groups. Sites can be in multiple groups.</span>
          </div>

          {/* Two-column layout */}
          <div className="two-column-layout">
            {/* LEFT: Groups */}
            <div className="groups-column">
              <h3 className="column-title">Groups</h3>
              <div className="groups-scroll-container">
                {groups.map(group => {
                  const selectedCount = (selectedSitesInGroups[group.id] || []).length;

                  return (
                    <Card key={group.id} className="group-card">
                      <div className="group-card-content">
                        <div className="group-header">
                          <span>{group.group_name}</span>
                          <span className="site-count">{group.sites?.length || 0} sites</span>
                        </div>

                        <div className="group-actions">
                          <GenericTooltip content="Edit Group Name" placement='left' tooltipStyle={{ background: 'black' }}>
                            <button
                              className="site-group-edit-btn"
                              onClick={() => {
                                setEditingGroup({
                                  id: group.id,
                                  name: group.group_name,
                                  newName: group.group_name
                                });
                                setIsEditModalOpen(true);
                              }}
                              disabled={isMoving || selectedCount > 0}
                            >
                              <i className="fa-solid fa-pen-to-square"></i>
                            </button>
                          </GenericTooltip>
                          
                          <GenericTooltip content={selectedCount > 0 ? `Remove (${selectedCount})` :  "Delete Group"} placement='top' tooltipStyle={{ background: 'black' }}>
                            <button
                              className="site-group-delete-btn"
                              onClick={selectedCount > 0 ? () => handleDeleteSitesFromGroup(group.id, group.group_name) : () => handleDeleteGroupWarning(group.id, group.group_name)}
                              disabled={isMoving}
                            >
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </GenericTooltip>

                          <GenericTooltip content="Clear selection" placement='right' tooltipStyle={{ background: 'black' }}>
                            <button
                              className="site-group-clear-btn"                                
                              onClick={() => clearGroupSelections(group.id)}
                              disabled={isMoving || selectedCount === 0}
                            >
                              <i className="fa-solid fa-undo"></i>
                            </button>
                          </GenericTooltip>

                        </div>

                        {/* Sites list with checkboxes */}
                        <div className="sites-list">
                          {group.sites && group.sites.length > 0 ? (
                            group.sites.map(siteObj => (
                              <label key={siteObj.site_name} className="site-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={(selectedSitesInGroups[group.id] || []).includes(siteObj.site_name)}
                                  onChange={() => toggleSiteSelectionInGroup(group.id, siteObj.site_name)}
                                  disabled={isMoving}
                                />
                                <span className="site-name">{siteObj.site_name}</span>
                              </label>
                            ))
                          ) : (
                            <p className="no-sites">No sites in this group</p>
                          )}
                        </div>
                       
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: Available Sites */}
            <div className="available-sites-column">
              <h3 className="column-title">Available Sites </h3>
              <Card className="available-sites-card">
                <div className="available-sites-content">
                  <div className="available-sites-header">

                      <label className="ungrouped-toggle">
                        <span>Ungrouped Sites</span>
                        <input
                          type="checkbox"
                          checked={showUngroupedOnly}
                          onChange={() => {
                            setShowUngroupedOnly(prev => !prev);
                            clearAvailableSelections(); // prevents hidden selections
                          }}
                        />
                        <span className="toggle-slider" />
                      </label>
                    

                    <span className="site-count">
                      {filteredAvailableSites.length} sites
                    </span>
                  </div>


                  <div className="sites-scroll-container">
                    {filteredAvailableSites.map(site => (
                      <label key={site} className="site-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedSitesInAvailable.includes(site)}
                          onChange={() => toggleSiteSelectionInAvailable(site)}
                          disabled={isMoving}
                        />
                        <span className="site-name">{site}</span>
                      </label>
                    ))}
                  </div>

                  {/* Action buttons for available sites */}
                  {selectedSitesInAvailable.length > 0 && (
                    <div className="action-buttons-container">
                      <div className="action-buttons">
                        <Button
                          variant='gradient'
                          size='sm'
                          onClick={() => {
                            setMovingFromGroup(null);
                            setMoveToGroupId('');
                            setIsMoveModalOpen(true);
                          }}
                          disabled={isMoving}
                          className='add-to-group-button'
                        >
                          <i className="fa-solid fa-plus"></i> Add to Group ({selectedSitesInAvailable.length})
                        </Button>
                        <Button
                          variant='secondary'
                          size='sm'
                          onClick={clearAvailableSelections}
                          className='add-to-group-button'
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Create Group Modal */}
      <Modal
        size='md'
        show={isCreateModalOpen}
        onHide={handleCloseCreateModal}
        position='center'
      >
        <Modal.Header>
          <Modal.Title>Create New Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isCreating && (
            <div className="modal-loading-overlay">
              <BeatLoader size={10} color="#36d7b7" />
              <p>Creating group "{newGroupName}"...</p>
            </div>
          )}

          <div className="form-group">
            <label>Group Name *</label>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="e.g., North Region, Downtown Sites"
              className="form-input"
              disabled={isCreating}
            />
          </div>

          <div className="form-group">
            <label>Select Sites * ({selectedSitesForNewGroup.length} selected)</label>
            <div className='available-sites-modal'>
              <label className="ungrouped-toggle-modal">
                <span>Ungrouped Sites</span>
                <input
                  type="checkbox"
                  checked={showUngroupedOnlyModal}
                  onChange={() => {
                    setShowUngroupedOnlyModal(prev => !prev);
                    clearAvailableSelections(); // prevents hidden selections
                  }}
                />
                <span className="toggle-slider-modal" />
              </label>
              <span className="site-count">
                {availableSitesForNewGroupFiltered.length} sites
              </span>
            </div>
            <div className="site-selection-list">
              <div className="site-selection-container">
  {/* Select All Checkbox */}
  {availableSitesForNewGroupFiltered.length > 0 && (
    <label className="site-checkbox-label select-all-label">
      <input
        type="checkbox"
        checked={
          availableSitesForNewGroupFiltered.length > 0 &&
          availableSitesForNewGroupFiltered.every(site => 
            selectedSitesForNewGroup.includes(site)
          )
        }
        onChange={(e) => {
          if (e.target.checked) {
            // Select all sites
            setSelectedSitesForNewGroup([
              ...new Set([
                ...selectedSitesForNewGroup,
                ...availableSitesForNewGroupFiltered
              ])
            ]);
          } else {
            // Deselect all filtered sites
            setSelectedSitesForNewGroup(
              selectedSitesForNewGroup.filter(
                site => !availableSitesForNewGroupFiltered.includes(site)
              )
            );
          }
        }}
        disabled={isCreating}
      />
      <span className='site-checkbox-label-modal select-all-text'>
        Select All ({availableSitesForNewGroupFiltered.length} sites)
      </span>
    </label>
  )}

  {/* Divider */}
  {availableSitesForNewGroupFiltered.length > 0 && (
    <div className="select-all-divider"></div>
  )}

  {/* Individual Site Checkboxes */}
  <div className="site-selection-list">
    {availableSitesForNewGroupFiltered.length > 0 ? (
      availableSitesForNewGroupFiltered.map(site => (
        <label key={site} className="site-checkbox-label">
          <input
            type="checkbox"
            checked={selectedSitesForNewGroup.includes(site)}
            onChange={() => toggleSiteSelection(site)}
            disabled={isCreating}
          />
          <span className='site-checkbox-label-modal'>{site}</span>
        </label>
      ))
    ) : (
      <p className="no-available-sites">
        No sites available.
      </p>
    )}
  </div>
</div>
            </div>
          </div>

          <div className="modal-actions">
           {(!newGroupName.trim() || selectedSitesForNewGroup.length === 0) && !isCreating ? (
              <GenericTooltip 
                content={
                  (() => {
                    const issues = [];
                    if (!newGroupName.trim()) issues.push("⚠️ Group name is required");
                    if (selectedSitesForNewGroup.length === 0) issues.push("⚠️ Select site(s) to create group");
                    return issues.join('\n');
                  })()
                }
                placement='top'
                tooltipStyle={{ 
                  background: 'rgba(239, 68, 68, 0.95)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#fef2f2',
                  whiteSpace: 'pre-line'
                }}
              >
                <Button
                  variant='gradient'
                  onClick={handleCreateGroup}
                  disabled={!newGroupName.trim() || selectedSitesForNewGroup.length === 0 || isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create Group'}
                </Button>
              </GenericTooltip>
            ) : (
              <Button
                variant='gradient'
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim() || selectedSitesForNewGroup.length === 0 || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Group'}
              </Button>
            )}
            <Button
              variant='secondary'
              onClick={handleCloseCreateModal}
              disabled={isCreating}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Move Sites Modal - UPDATED: Using custom Select component */}
      <Modal
        size='sm'
        show={isMoveModalOpen}
        onHide={handleCloseMoveModal}
        position='center'
      >
        <Modal.Header>
          <Modal.Title>
            {movingFromGroup ? 'Add Sites to Another Group' : 'Add Sites to Group'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Select
            label="Select Target Group *"
            value={moveToGroupId}
            onChange={(e) => setMoveToGroupId(e.target.value)}
            options={moveToGroupOptions}
            placeholder="-- Select Group --"
            size="md"
          />

          <div className="modal-actions">
            <Button
              variant='gradient'
              onClick={movingFromGroup ? handleMoveSelectedSites : handleAddSitesToGroup}
              disabled={!moveToGroupId}
            >
              Add to Group
            </Button>
            <Button
              variant='secondary'
              onClick={handleCloseMoveModal}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Edit Group Name Modal */}
      <Modal
        size='sm'
        show={isEditModalOpen}
        onHide={handleCloseEditModal}
        position='center'
      >
        <Modal.Header>
          <Modal.Title>Edit Group Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isEditing && (
            <div className="modal-loading-overlay">
              <div className="modal-loading-content">
                <BeatLoader size={10} color="#36d7b7" />
                <p>Updating group name...</p>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Group Name *</label>
            <input
              type="text"
              value={editingGroup?.newName || ''}
              onChange={(e) => setEditingGroup({ ...editingGroup, newName: e.target.value })}
              placeholder="Enter new group name"
              className="form-input"
              disabled={isEditing}
            />
          </div>

          <div className="modal-actions">
            <Button
              variant='gradient'
              onClick={handleUpdateGroupName}
              disabled={!editingGroup?.newName?.trim() || isEditing}
            >
              {isEditing ? 'Updating...' : 'Update Name'}
            </Button>
            <Button
              variant='secondary'
              onClick={handleCloseEditModal}
              disabled={isEditing}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Warning Modal for Ending All Grouping */}
      <Modal
        size='sm'
        show={isWarningEndAllGroupModalOpen}
        onHide={handleCloseWarningEndAllGroupModal}
        position='center'
      >
        <Modal.Header>
          <Modal.Title>Confirm End All Grouping</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isDeletingAll && (
            <div className="modal-loading-overlay">
              <div className="modal-loading-content">
                <BeatLoader size={10} color="#36d7b7" />
                <p>Ending all grouping...</p>
              </div>
            </div>
          )}

          <div className='end-all-group-warning-text'>Are you sure you want to delete ALL groups?</div>
          <div className="end-grouping-modal-actions">
            <Button
              variant='danger'
              onClick={() => handleEndAllGroup()}
              disabled={isDeletingAll}
            >
              {isDeletingAll ? 'Deleting...' : 'Yes, End All Grouping'}
            </Button>
            <Button
              variant='secondary'
              onClick={handleCloseWarningEndAllGroupModal}
              disabled={isDeletingAll}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

    {/* Warning Modal to remove a site from its Group */}
    <Modal
    size="sm"
    show={isRemoveSitesModalOpen}
    onHide={() => !isRemovingSites && setIsRemoveSitesModalOpen(false)}
    centered
    >
      <Modal.Header>
        <Modal.Title>Confirm Remove Sites</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isRemovingSites && (
          <div className="modal-loading-overlay">
            <div className="modal-loading-content">
              <BeatLoader size={10} color="#36d7b7" />
              <p>
                Removing {selectedSitesInGroups[removingGroup?.groupId]?.length} site(s)
                from "{removingGroup?.groupName}"...
              </p>
            </div>
          </div>
        )}

        <p>
          Are you sure you want to remove{" "}
          <strong>
            {selectedSitesInGroups[removingGroup?.groupId]?.length}
          </strong>{" "}
          site(s) from group "<strong>{removingGroup?.groupName}</strong>"?
        </p>

        <div className="end-grouping-modal-actions">
          <Button
            variant="danger"
            onClick={confirmRemoveSitesFromGroup}
            disabled={isRemovingSites}
          >
            {isRemovingSites
              ? 'Removing...'
              : `Yes, Remove Sites`}
          </Button>

          <Button
            variant="secondary"
            onClick={() => setIsRemoveSitesModalOpen(false)}
            disabled={isRemovingSites}
          >
            Cancel
          </Button>
        </div>
      </Modal.Body>
    </Modal>


      {/* Warning Modal for ending a specific group */}
      <Modal
        size='sm'
        show={isWarningEndGroupModalOpen}
        onHide={handleCloseWarningEndGroupModal}
        position='center'
      >
        <Modal.Header>
          <Modal.Title>Confirm End Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isDeleting && (
            <div className="modal-loading-overlay">
              <div className="modal-loading-content">
                <BeatLoader size={10} color="#36d7b7" />
                <p>Deleting group "{deletingGroup?.groupName}"...</p>
              </div>
            </div>
          )}

          <div className='end-a-group-warning-text'>{`Are you sure you want to end Group "${deletingGroup?.groupName}"?`}</div>
          <div className="end-grouping-modal-actions">
            <Button
              variant='danger'
              onClick={() => handleEndGroup()}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : `Yes, End Group "${deletingGroup?.groupName}"`}
            </Button>
            <Button
              variant='secondary'
              onClick={handleCloseWarningEndGroupModal}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default SiteGroupManager;